import React, { createContext, useState, useMemo, useEffect, useCallback } from "react";
import { createTheme, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";

export const ThemeContext = createContext({
  mode: "light",
  toggleMode: () => {},
});

const lightPalette = {
  mode: "light",
  primary: {
    main: "#1976d2",
  },
  background: {
    default: "#f5f5f5",
    paper: "#fff",
  },
  text: {
    primary: "#000",
    secondary: "#555",
  },
};

const darkPalette = {
  mode: "dark",
  primary: {
    main: "#90caf9",
  },
  background: {
    default: "#121212",
    paper: "#1e1e1e",
  },
  text: {
    primary: "#fff",
    secondary: "#aaa",
  },
};

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState("light");

  // Load theme mode from localStorage on mount
  useEffect(() => {
    try {
      const savedMode = localStorage.getItem("appThemeMode");
      if (savedMode === "light" || savedMode === "dark") {
        setMode(savedMode);
      }
    } catch {
      console.error("Failed to load theme mode from localStorage");
    }
  }, []);

  // toggleMode function with useCallback to avoid unnecessary re-creations
  const toggleMode = useCallback(() => {
    setMode((prevMode) => {
      const nextMode = prevMode === "light" ? "dark" : "light";
      try {
        localStorage.setItem("appThemeMode", nextMode);
      } catch {
        console.error("Failed to save theme mode to localStorage");
      }
      return nextMode;
    });
  }, []);

  const theme = useMemo(() => {
    const palette = mode === "light" ? lightPalette : darkPalette;
    return createTheme({
      palette,
      typography: {
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
      },
    });
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}